import { Calendar } from 'lucide-react';

export type PeriodPreset =
  | 'prev_year' | 'prev_quarter' | 'prev_month'
  | 'curr_year' | 'curr_quarter' | 'curr_month'
  | 'next_year' | 'next_quarter' | 'next_month'
  | 'custom';

export interface DateRange {
  from: string; // YYYY-MM-DD
  to:   string; // YYYY-MM-DD
}

function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

export function getPresetRange(preset: PeriodPreset): DateRange {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth() + 1; // 1-based
  const q     = Math.ceil(month / 3);

  switch (preset) {
    case 'prev_year':
      return { from: isoDate(year - 1, 1, 1), to: isoDate(year - 1, 12, 31) };
    case 'prev_quarter': {
      const pq = q === 1 ? 4 : q - 1;
      const py = q === 1 ? year - 1 : year;
      const sm = (pq - 1) * 3 + 1;
      const em = sm + 2;
      return { from: isoDate(py, sm, 1), to: isoDate(py, em, daysInMonth(py, em)) };
    }
    case 'prev_month': {
      const pm = month === 1 ? 12 : month - 1;
      const py = month === 1 ? year - 1 : year;
      return { from: isoDate(py, pm, 1), to: isoDate(py, pm, daysInMonth(py, pm)) };
    }
    case 'curr_year':
      return { from: isoDate(year, 1, 1), to: isoDate(year, 12, 31) };
    case 'curr_quarter': {
      const sm = (q - 1) * 3 + 1;
      const em = sm + 2;
      return { from: isoDate(year, sm, 1), to: isoDate(year, em, daysInMonth(year, em)) };
    }
    case 'curr_month':
      return { from: isoDate(year, month, 1), to: isoDate(year, month, daysInMonth(year, month)) };
    case 'next_year':
      return { from: isoDate(year + 1, 1, 1), to: isoDate(year + 1, 12, 31) };
    case 'next_quarter': {
      const nq = q === 4 ? 1 : q + 1;
      const ny = q === 4 ? year + 1 : year;
      const sm = (nq - 1) * 3 + 1;
      const em = sm + 2;
      return { from: isoDate(ny, sm, 1), to: isoDate(ny, em, daysInMonth(ny, em)) };
    }
    case 'next_month': {
      const nm = month === 12 ? 1 : month + 1;
      const ny = month === 12 ? year + 1 : year;
      return { from: isoDate(ny, nm, 1), to: isoDate(ny, nm, daysInMonth(ny, nm)) };
    }
    default:
      return { from: '', to: '' };
  }
}

const PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: 'prev_year',    label: 'שנה קודמת'    },
  { key: 'prev_quarter', label: 'רבעון קודם'   },
  { key: 'prev_month',   label: 'חודש קודם'    },
  { key: 'curr_year',    label: 'שנה נוכחית'   },
  { key: 'curr_quarter', label: 'רבעון נוכחי'  },
  { key: 'curr_month',   label: 'חודש נוכחי'   },
  { key: 'next_year',    label: 'שנה הבאה'     },
  { key: 'next_quarter', label: 'רבעון הבא'    },
  { key: 'next_month',   label: 'חודש הבא'     },
];

interface PeriodFilterProps {
  from:         string;
  to:           string;
  activePreset: PeriodPreset | null;
  onChange:     (range: DateRange, preset: PeriodPreset | null) => void;
}

export function PeriodFilter({ from, to, activePreset, onChange }: PeriodFilterProps) {
  const handlePreset = (key: PeriodPreset) => {
    onChange(getPresetRange(key), key);
  };

  const handleCustomFrom = (val: string) => {
    onChange({ from: val, to }, null);
  };

  const handleCustomTo = (val: string) => {
    onChange({ from, to: val }, null);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" dir="rtl">
      {/* Preset buttons */}
      {PRESETS.map(p => (
        <button
          key={p.key}
          type="button"
          onClick={() => handlePreset(p.key)}
          className={`
            px-2.5 py-1 text-xs font-medium rounded-full border transition-colors whitespace-nowrap
            ${activePreset === p.key
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'}
          `}
        >
          {p.label}
        </button>
      ))}

      {/* Divider */}
      <div className="h-5 w-px bg-gray-200 mx-1" />

      {/* Custom date range */}
      <div className="flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input
          type="date"
          value={from}
          onChange={e => handleCustomFrom(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <span className="text-xs text-gray-400">—</span>
        <input
          type="date"
          value={to}
          onChange={e => handleCustomTo(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}
